import {Button, Pagination, Tooltip, Select, Spin} from 'antd';
import {Table, Switch, Space} from 'antd';
import {LoadingOutlined} from "@ant-design/icons";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {v4} from "uuid";
import {getGridTotal, useDataGrid} from "./DataGridContext";
import {useTranslation} from "react-i18next";
import {EyeOutlined, ReloadOutlined, UnorderedListOutlined} from "@ant-design/icons";
import {useModal} from "../../context/ModalContext";
import ColumnFilter from "../ColumnFilter";
import MobileCardList from "./MobileCardList";
import { useIsSmallScreen } from '../../utils/useMediaQuery';
import { createVirtualBody } from "./VirtualBody";


const SORT = {
    NONE: -1,
    ASC: 0,
    DESC: 1
}

// Fallback width for columns that don't declare one, used only in virtual mode
// (fixed layout requires a fully-sized colgroup for header/body to align).
const DEFAULT_VIRTUAL_COL_WIDTH = 150;

// Sayfa boyutu seçenekleri. Üst sınır 500'dür ve sunucu da aynı sınırı uygular
// (RepositoryExtension.MaxPageSize) — "Tümü" seçeneği kaldırıldı.
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 250, 500];

// Sanal grid gövdesinin (scroll.y) yüksekliği. Sabit kabuk (header + filtre satırı +
// araç çubuğu + sayfalama) için ~300px pay bırakılır. Ayrıca alt bant (footer) SABİT
// konumlu ve içerik alanı ondan `--footer-h + 2px` kadar dolgu ayırıyor; footer
// yüksekliğini de düşmezsek sayfa tam footer boyu kadar taşıp dikey scroll çıkıyor.
const GRID_CHROME_OFFSET = 300;
export function getGridBodyHeight() {
    let footer = 50; // --footer-h (48) + amber üst çizgi (2); CSS okunamazsa yedek
    if (typeof document !== 'undefined') {
        const px = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--footer-h'),
            10
        );
        if (Number.isFinite(px)) footer = px + 2;
    }
    return window.innerHeight - GRID_CHROME_OFFSET - footer;
}

export const RenderableExpandRow = ({children, expanded, recordId}) => {

    const {t} = useTranslation();
    const {renderItem, renderExpandedRow, refreshDataGrid} = useDataGrid()
    const [item, setItem] = useState('');

    useEffect(() => {
        if (renderItem && renderItem.recordId === recordId) {
            setItem(renderItem)
        }
    }, [renderItem])

    return (
        <>
            {
                expanded && (
                    <div key={item.key}>
                        {/*<Tooltip placement={'bottomLeft'} title={t('Refresh')}>*/}
                        {/*    <Button onClick={() => {*/}
                        {/*        renderExpandedRow(recordId)*/}
                        {/*    }}>*/}
                        {/*        <ReloadOutlined/>*/}
                        {/*    </Button>*/}
                        {/*</Tooltip>*/}
                        {children}
                    </div>
                )
            }
        </>
    )
}

export default function DataGrid({title, name, mobileCardTitle}) {
    const {t} = useTranslation();
    const {
        resultModel,
        dataGridProps,
        setSelectedRows,
        onFilterPageSort,
        tableId,
        selectedRows,
        queryModel,
        focusOut,
        refreshDataGrid,
        count,
        loadExactCount
    } = useDataGrid()
    const [columns, setColumns] = useState([])
    const {openModal, closeModal} = useModal()
    const isMobile = useIsSmallScreen();

    const rowKey = dataGridProps.rowKey || 'id';
    const virtual = !!dataGridProps.virtual;

    // Measured content width of the virtual table body, used to scale columns to
    // fill the screen.
    const tableWrapRef = useRef(null);
    const [availableWidth, setAvailableWidth] = useState(0);

    useEffect(() => {
        if (!virtual) return;
        const wrap = tableWrapRef.current;
        if (!wrap || typeof ResizeObserver === 'undefined') return;

        const measure = () => {
            const body = wrap.querySelector('.ant-table-body');
            const w = body ? body.clientWidth : wrap.clientWidth;
            if (w) setAvailableWidth(prev => (prev === w ? prev : w));
        };

        // Yeni genişlik BOYAMADAN ÖNCE uygulanmalı. Kolon genişlikleri (ve scroll.x)
        // bu ölçümden türetiliyor; ölçümü bir sonraki kareye bırakırsak o kare boyunca
        // tablo eski/geniş kolonlarla daralmış kapsayıcıya çizilir, sığmadığı için
        // .ant-table-body'de yatay scrollbar çıkar ve hemen kaybolur → filtre panelini
        // açıp kapatırken flicker. ResizeObserver layout'tan sonra, paint'ten önce
        // çalışıyor; flushSync ile render'ı aynı karede bitirip ara kareyi yok ediyoruz.
        // (Effect içindeki ilk çağrı flushSync'siz: lifecycle içinde flushSync uyarı verir.)
        const measureBeforePaint = () => flushSync(measure);

        measure();
        // Pencere yeniden boyutlandırmada wrap'ın genişliği de değiştiği için ayrı bir
        // window 'resize' dinleyicisi gerekmez; olurdu ise aynı karede ikinci bir
        // senkron render'a yol açardı.
        const ro = new ResizeObserver(measureBeforePaint);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [virtual, resultModel.data]);

    // Latest values accessible from the (stable) rowSelection callbacks without
    // rebuilding them.
    const setSelectedRowsRef = useRef(setSelectedRows);
    setSelectedRowsRef.current = setSelectedRows;
    const dataRef = useRef(resultModel.data);
    dataRef.current = resultModel.data;
    const selectedRowsRef = useRef(selectedRows);
    selectedRowsRef.current = selectedRows;

    // Shift-range selection: anchor = last plainly (un-shifted) toggled row;
    // shiftActionRef carries the pending range from onSelect to onChange.
    const anchorIndexRef = useRef(null);
    const shiftActionRef = useRef(null);

    // Derive selected keys from the single source of truth (context.selectedRows)
    // instead of a second state copy + sync effect, which double-rendered the
    // whole table on every checkbox click.
    const selectedRowKeys = useMemo(
        () => selectedRows.map(x => x[rowKey]),
        [selectedRows, rowKey]
    );

    // Fires before onChange; record whether this click is a shift-range and set
    // the anchor otherwise.
    const handleSelect = useCallback((record, selected, _rows, nativeEvent) => {
        const data = dataRef.current || [];
        const idx = data.findIndex(r => r[rowKey] === record[rowKey]);
        if (nativeEvent?.shiftKey && anchorIndexRef.current != null && idx !== -1) {
            shiftActionRef.current = { from: anchorIndexRef.current, to: idx, select: selected };
        } else {
            shiftActionRef.current = null;
            anchorIndexRef.current = idx;
        }
    }, [rowKey]);

    const handleSelectAll = useCallback(() => {
        shiftActionRef.current = null;
        anchorIndexRef.current = null;
    }, []);

    const handleSelectionChange = useCallback((_selectedRowKeys, rows) => {
        const action = shiftActionRef.current;
        shiftActionRef.current = null;

        if (action) {
            // Apply the range [from..to] on top of the existing selection.
            const data = dataRef.current || [];
            const [lo, hi] = action.from <= action.to ? [action.from, action.to] : [action.to, action.from];
            const keySet = new Set((selectedRowsRef.current || []).map(r => r[rowKey]));
            for (let i = lo; i <= hi; i++) {
                const r = data[i];
                if (!r) continue;
                if (action.select) keySet.add(r[rowKey]);
                else keySet.delete(r[rowKey]);
            }
            setSelectedRowsRef.current(data.filter(r => keySet.has(r[rowKey])));
            anchorIndexRef.current = action.to;
            return;
        }

        setSelectedRowsRef.current(rows);
    }, [rowKey]);

    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: handleSelectionChange,
        onSelect: handleSelect,
        onSelectAll: handleSelectAll,
        // Explicit width so the colgroup is fully specified. With a fixed table
        // layout + virtualization, an unsized selection column makes the header
        // and body compute its width differently and misaligns the columns.
        columnWidth: 40,
    }), [selectedRowKeys, handleSelectionChange, handleSelect, handleSelectAll]);

    // Reset the shift-selection anchor whenever the data changes (its row indices
    // would otherwise be stale).
    useEffect(() => {
        anchorIndexRef.current = null;
        shiftActionRef.current = null;
    }, [resultModel.data]);

    // Row virtualization: only mount the visible rows. Keeps native rows so row
    // colors / selection / onRow are unaffected. Requires scroll.y (set by the
    // caller's antdTableProps).
    const virtualComponents = useMemo(
        () => virtual
            ? {body: {wrapper: createVirtualBody(dataGridProps.virtualRowHeight || 39)}}
            : undefined,
        [virtual, dataGridProps.virtualRowHeight]
    );

    useEffect(() => {
        if (dataGridProps.columns) {
            const selectedCols = JSON.parse(localStorage.getItem(`data-grid-${name}`) || '[]')
            setColumns(
                dataGridProps.columns
                    .filter(x => {
                        return selectedCols.find(y => y.key === x.key) || selectedCols.length === 0
                    })
                    .map(x => {
                        x.sortOrder = null
                        return x
                    })
            )
        }
    }, [dataGridProps.columns])
    // Keep the header sort arrows in sync with the ACTUAL query order on every
    // query change (filter/sort/refresh). Previously this depended on
    // `queryModel.id` — a field the reducer never sets (it sets `updateKey`) — so
    // it was dead code and the arrows could linger after the sort was reset,
    // making the table look sorted while the results were not. Deriving the arrow
    // straight from `orderByItems` makes the two impossible to disagree.
    useEffect(() => {
        const items = queryModel.orderByItems || []
        setColumns(cols => {
            let changed = false
            cols.forEach(x => {
                const item = items.find(o =>
                    o.name === x.columnName || o.name === x.key || o.name === x.dataIndex)
                const sortOrder = item
                    ? (item.operation === SORT.ASC ? 'ascend' : 'descend')
                    : null
                if (x.sortOrder !== sortOrder) {
                    x.sortOrder = sortOrder
                    changed = true
                }
            })
            return changed ? [...cols] : cols
        })
    }, [queryModel.updateKey])

    // Study/Report/HL7'de sayım ayrı istekle gelir (grid cevabı totalRecords = -1); diğer grid'lerde
    // eskisi gibi grid cevabındadır. Farkı getGridTotal soğurur.
    const {total: gridTotal, deferred: deferredCount, capped, known} = getGridTotal(resultModel, count);

    // Kaç satır seçili olduğu toplamın yanında gösterilir: toplu işlem yapmadan önce
    // kullanıcı kaç kayda dokunacağını görsün. Sayfa/filtre değişince seçim sıfırlandığı
    // için (bkz. ON_FILTER_PAGE_SORT) bu sayı daima açık sayfaya aittir.
    // Ek render maliyeti yok: DataGrid zaten selectedRows'a abone (selectedRowKeys/rowSelection)
    // ve her seçim değişiminde yeniden render oluyor.
    const selectedCount = dataGridProps.enableRowSelection ? selectedRows.length : 0;

    const renderTotalText = (total) => {
        if (!deferredCount)
            return `Toplam ${total} kayıt`;

        // Sayım henüz gelmedi: tam sayfa (request-spinner) yerine burada — "gerçek sayıyı getir"
        // butonunun çıkacağı yerde — küçük bir yerel spinner göster.
        if (!known)
            return (
                <span>
                    {'Toplam '}
                    <Spin indicator={<LoadingOutlined spin/>} size="small" style={{margin: '0 4px'}}/>
                    {' kayıt'}
                </span>
            );

        // Tavana değdiyse gerçek sayı daha büyüktür: "500+" ve kesin sayımı isteme seçeneği.
        if (capped) {
            return (
                <span>
                    {`Toplam ${count.value}+ kayıt`}
                    <Button className={'btn-accent'} size={'small'} loading={count.loading} onClick={loadExactCount}>
                        {t('getExactCount')}
                    </Button>
                </span>
            );
        }

        return `Toplam ${count.value} kayıt`;
    };

    const renderTotal = (total) => (
        <>
            {renderTotalText(total)}
            {selectedCount > 0 && (
                <span style={{marginInlineStart: 8, color: 'var(--accent-dark)', fontWeight: 600}}>
                    {t('gridSelectedRowCount', {count: selectedCount})}
                </span>
            )}
        </>
    );

    const getSortType = (sort) => {
        if (!sort)
            return SORT.NONE;
        else if (sort === 'ascend')
            return SORT.ASC
        else
            return SORT.DESC
    }

    const onChange = (pagination, filters, sorter) => {
        const _columns = [...columns].map(x => {
            x.sortOrder = ''
            return x;
        })

        const orderByItems = []
        const col = _columns.find(x => x.key === sorter.field);
        if (sorter.order && col) {
            col.sortOrder = sorter.order
            orderByItems.push({
                name: col.columnName || sorter.field,
                operation: getSortType(sorter.order)
            })
        } else {
            if(col) {
                col.sortOrder = null
            }
        }

        const paginationQuery = {
            pageNumber: pagination.current,
            pageSize: pagination.pageSize
        };

        onFilterPageSort({
            filter: queryModel.filter,
            pagination: paginationQuery,
            orderByItems
        })

        setColumns(_columns)
    }

    // Give every column a default `shouldCellUpdate` keyed on record identity.
    // antd regenerates the columns array on every selection change, which makes
    // rc-table's MemoCell fall back to shallowEqual and re-run EVERY cell's
    // render (rows × columns). With this, a selection change skips the expensive
    // cell renders and only the checkbox column updates. Data updates replace
    // records with new objects, so real changes still render.
    const tableColumns = useMemo(
        () => columns.map(col => {
            const withUpdate = col.shouldCellUpdate
                ? col
                : {...col, shouldCellUpdate: (record, prevRecord) => record !== prevRecord};
            // Virtualization needs a fixed layout with EVERY column sized, else the
            // header and body compute widths differently and misalign. Give any
            // width-less column a sane default so any grid can just flip on
            // `virtual` without auditing its columns.
            if (virtual && !withUpdate.width) {
                return {...withUpdate, width: DEFAULT_VIRTUAL_COL_WIDTH};
            }
            return withUpdate;
        }),
        [columns, virtual]
    );

    // Treat the entered widths as ratios: on a screen wider than their sum, scale
    // every column up proportionally so the table fills the width (no dead gap on
    // the right). On narrower screens keep the entered widths (horizontal scroll).
    // `availableWidth` is the real content width of the body (measured below).
    const selWidth = dataGridProps.enableRowSelection ? 40 : 0;
    const scaledColumns = useMemo(() => {
        if (!virtual || !availableWidth) return tableColumns;

        const baseSum = tableColumns.reduce((s, c) => s + (c.width || DEFAULT_VIRTUAL_COL_WIDTH), 0);
        const dataTarget = availableWidth - selWidth;
        if (baseSum <= 0 || dataTarget <= baseSum) return tableColumns; // narrow → keep

        const scale = dataTarget / baseSum;
        const scaled = tableColumns.map(c => ({
            ...c,
            width: Math.floor((c.width || DEFAULT_VIRTUAL_COL_WIDTH) * scale),
        }));

        // Give the rounding leftover to the last column so it fills exactly.
        const used = scaled.reduce((s, c) => s + c.width, 0);
        const leftover = dataTarget - used;
        if (leftover > 0 && scaled.length) {
            scaled[scaled.length - 1] = {
                ...scaled[scaled.length - 1],
                width: scaled[scaled.length - 1].width + leftover,
            };
        }
        return scaled;
    }, [virtual, tableColumns, availableWidth, selWidth]);

    // In virtual mode, DataGrid owns the table sizing: a complete-width colgroup,
    // a matching scroll.x, fixed layout, and a scroll.y (from the caller or a
    // sensible default) that creates the scroll container we virtualize inside.
    const {scroll: callerScroll, tableLayout: callerLayout, ...restAntdProps} = dataGridProps.antdTableProps || {};
    const mergedScroll = useMemo(() => {
        if (!virtual) return callerScroll || {x: 1280, y: getGridBodyHeight()};
        const totalWidth = selWidth + scaledColumns.reduce((sum, c) => sum + (c.width || DEFAULT_VIRTUAL_COL_WIDTH), 0);
        const y = callerScroll?.y ?? getGridBodyHeight();
        return {x: totalWidth, y};
    }, [virtual, callerScroll, scaledColumns, selWidth]);
    const mergedTableLayout = virtual ? 'fixed' : callerLayout;

    // Render both but hide one based on screen size
    // Table must stay in DOM to prevent useEffect infinite loop
    // Only show mobile view if mobileCardTitle is provided
    const showMobileView = isMobile && mobileCardTitle;
    
    return (
        <>
            {
                showMobileView && (
                    <MobileCardList
                        name={name}
                        title={title}
                        mobileCardTitle={mobileCardTitle}
                        rowClassName={dataGridProps.rowClassName}
                    />
                )
            }
            <div ref={tableWrapRef} className={`app-data-grid app-data-grid--${name}`} style={{ display: showMobileView ? 'none' : 'block' }}>
                <Table
                    size={'small'}
                    scroll={mergedScroll}
                    tableLayout={mergedTableLayout}
                    rowKey={rowKey}
                    columns={scaledColumns}
                    rowSelection={dataGridProps.enableRowSelection ? rowSelection : null}
                    dataSource={resultModel.data}
                    onChange={onChange}
                    showSorterTooltip={false}
                    expandable={virtual ? undefined : {
                        expandedRowRender: dataGridProps.expandedRowRender,
                        rowExpandable: dataGridProps.rowExpandable,
                    }}
                    rowClassName={dataGridProps.rowClassName}
                    onRow={dataGridProps.onRow}
                    id={tableId}
                    title={() => {
                        return (
                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <Button
                                    type={'text'}
                                    size={'small'}
                                    onClick={() => {
                                        openModal(t('Hide/Show Columns'), (
                                            <ColumnFilter
                                                columns={dataGridProps.columns}
                                                selectedColumnKeys={columns.map(x => x.key)}
                                                onSelectedColumns={(cols) => {
                                                    setColumns(cols)
                                                    localStorage.setItem(`data-grid-${name}`, JSON.stringify(cols))
                                                    closeModal()
                                                }}
                                            />
                                        ))
                                    }}
                                >
                                    <UnorderedListOutlined />
                                </Button>
                                <Button
                                    size={'small'}
                                    onClick={() => refreshDataGrid()}
                                >
                                    <ReloadOutlined />
                                </Button>
                                {title && title()}
                            </div>
                        )
                    }}
                    pagination={{
                        total: gridTotal,
                        pageSize: resultModel.pagination?.pageSize || 10,
                        size: 'small',
                        current: resultModel.pagination?.pageNumber || 1,
                        showTotal: renderTotal,
                        showSizeChanger: true,
                        hideOnSinglePage: false,
                        pageSizeOptions: PAGE_SIZE_OPTIONS
                    }}
                    {...restAntdProps}
                    {...(virtual ? {components: virtualComponents} : {})}
                />
            </div>
        </>
    )
}
